import { useEffect, useId, useRef, useState } from "react";
import { type Field, type MapFormServices, type Values } from "@proof/core";
import { AssetImage } from "./AssetImage";
import { MapPicker } from "./MapPicker";
import type { AssetLoader } from "./images";
export function MapEditor({
  field,
  values,
  services,
  scope,
  disabled,
  onChange,
  assetUrl,
  loadAsset,
}: {
  field: Field;
  values: Values;
  services: MapFormServices;
  scope: string;
  disabled: boolean;
  onChange: (value: Values[string]) => void;
  assetUrl?: (id: string) => string;
  loadAsset?: AssetLoader;
}) {
  const [editing, setEditing] = useState<string>();
  useEffect(() => setEditing(undefined), [scope, disabled, services]);
  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={() => setEditing(scope)}
      >
        {disabled ? "View map" : "Edit map"}
      </button>
      {editing === scope && (
        <MapDialog
          field={field}
          values={values}
          services={services}
          scope={scope}
          disabled={disabled}
          assetUrl={assetUrl}
          loadAsset={loadAsset}
          onClose={() => setEditing(undefined)}
          onApply={(value) => {
            setEditing(undefined);
            if (!disabled) onChange(value);
          }}
        />
      )}
    </>
  );
}
function MapDialog({
  field,
  values,
  services,
  scope,
  disabled,
  onClose,
  onApply,
  assetUrl,
  loadAsset,
}: {
  field: Field;
  values: Values;
  services: MapFormServices;
  scope: string;
  disabled: boolean;
  onClose: () => void;
  onApply: (value: Values[string]) => void;
  assetUrl?: (id: string) => string;
  loadAsset?: AssetLoader;
}) {
  const dialog = useRef<HTMLDialogElement>(null),
    title = useId();
  const [selection, setSelection] = useState<Values[string]>();
  const current = selection || values[field.id];
  const assetId =
    typeof current === "string"
      ? current
      : current &&
          typeof current === "object" &&
          !Array.isArray(current) &&
          typeof current.assetId === "string"
        ? current.assetId
        : undefined;
  const editorField =
    field.map?.locationMode === "image"
      ? { ...field, map: { ...field.map, locationMode: "address" as const } }
      : field;
  useEffect(() => {
    const element = dialog.current!;
    element.showModal();
    return () => {
      if (element.open) element.close();
    };
  }, []);
  const close = () => {
    dialog.current?.close();
    onClose();
  };
  return (
    <dialog
      ref={dialog}
      aria-labelledby={title}
      onCancel={(e) => {
        e.preventDefault();
        e.stopPropagation();
        close();
      }}
      onClose={(e) => {
        e.stopPropagation();
        // StrictMode reopens after cleanup before its queued close event fires.
        if (!e.currentTarget.open) onClose();
      }}
      onKeyDown={(e) => {
        if (["Escape", "Tab"].includes(e.key)) e.stopPropagation();
        if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      style={{
        width: "min(880px,calc(100vw - 32px))",
        maxHeight: "calc(100dvh - 32px)",
        overflow: "auto",
        boxSizing: "border-box",
        border: "1px solid var(--color-border,#c6cfca)",
        borderRadius: 12,
        padding: 24,
        background: "var(--color-surface,#fff)",
        color: "var(--color-text,#22312b)",
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 16,
          alignItems: "center",
        }}
      >
        <h2 id={title}>
          {disabled ? "View map" : "Edit map"} · {field.label}
        </h2>
        <button type="button" aria-label="Close map editor" onClick={close}>
          ×
        </button>
      </header>
      <p>
        Start with the mapped address or enter an address or coordinates.
        Generate a map, review it, then apply it to your form.
      </p>
      <MapPicker
        field={editorField}
        values={{ ...values, [field.id]: current }}
        services={services}
        scope={scope}
        disabled={disabled}
        onChange={setSelection}
      />
      {assetId && (
        <div style={{ margin: "20px auto", maxWidth: 640 }}>
          <AssetImage
            assetId={assetId}
            assetUrl={assetUrl}
            loadAsset={loadAsset}
            className="asset-thumbnail"
            alt={`${field.label} map preview`}
          />
        </div>
      )}
      <footer
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          marginTop: 20,
        }}
      >
        <button type="button" onClick={close}>
          {disabled ? "Close" : "Cancel"}
        </button>
        {!disabled && (
          <button
            type="button"
            disabled={!selection}
            onClick={() => {
              if (selection !== undefined) onApply(selection);
            }}
          >
            Apply map
          </button>
        )}
      </footer>
    </dialog>
  );
}
